import React, { useEffect, useState, useRef, useCallback } from 'react';
import apiClient from '@/utils/api';
import { useAuth } from '@/App';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { INCENTIVE_ROLES } from './IncentivePolicySettings';
import { RoleIncentiveStudentCard, RoleIncentiveDialog, incentiveMoney } from './RoleIncentiveWidgets';

const PAGE = 25;
const roleName = role => INCENTIVE_ROLES.find(([k]) => k === role)?.[1] || role;

export const RoleIncentiveLedger = () => {
  const { user } = useAuth();
  const office = ['admin', 'operating_head', 'accountant'].includes(user?.role);
  const canReview = ['admin', 'operating_head'].includes(user?.role);
  const canPay = ['admin', 'operating_head', 'accountant'].includes(user?.role);
  const personal = !office;

  const [data, setData] = useState(null), [students, setStudents] = useState([]), [error, setError] = useState(false);
  const [role, setRole] = useState('all'), [state, setState] = useState('all'), [employee, setEmployee] = useState('all');
  const [skip, setSkip] = useState(0), [loadingMore, setLoadingMore] = useState(false), [showEmployees, setShowEmployees] = useState(false);
  const [modal, setModal] = useState(null), [busy, setBusy] = useState(false);
  const [reference, setReference] = useState(''), [candidate, setCandidate] = useState(''), [method, setMethod] = useState('bank_transfer'), [users, setUsers] = useState([]);
  const request = useRef(0);

  const fetchPage = useCallback(async (nextSkip) => {
    const params = { skip: nextSkip, limit: PAGE };
    if (office) {
      if (role !== 'all') params.role = role;
      if (employee !== 'all') params.employee_id = employee;
      if (state !== 'all') params.status = state;
    }
    return (await apiClient.get('/finance/role-incentives', { params })).data;
  }, [office, role, employee, state]);

  const load = useCallback(async () => {
    const id = ++request.current; setError(false);
    try {
      const result = await fetchPage(0);
      if (id !== request.current) return;
      setData(result); setStudents(result.students || []); setSkip(0);
    } catch { if (id === request.current) setError(true); }
  }, [fetchPage]);

  useEffect(() => { setData(null); setStudents([]); load(); return () => { request.current++; }; }, [load, user?.id, user?.role]);
  useEffect(() => {
    const refresh = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', refresh); window.addEventListener('incentives-changed', refresh);
    const timer = setInterval(refresh, 60000);
    return () => { clearInterval(timer); window.removeEventListener('focus', refresh); window.removeEventListener('incentives-changed', refresh); };
  }, [load]);

  const loadMore = async () => {
    if (loadingMore || !data?.has_more) return;
    setLoadingMore(true);
    try {
      const next = skip + PAGE;
      const result = await fetchPage(next);
      setStudents(prev => [...prev, ...(result.students || [])]);
      setData(result); setSkip(next);
    } catch { toast.error('Could not load more'); }
    finally { setLoadingMore(false); }
  };

  const act = async (row, type) => {
    if (type === 'approve' || type === 'restore') {
      try {
        await apiClient.post(`/finance/role-incentives/${encodeURIComponent(row.id)}/${type}`);
        toast.success(type === 'approve' ? 'Incentive approved' : 'Line restored'); await load(); window.dispatchEvent(new Event('incentives-changed'));
      } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
      return;
    }
    setReference(''); setCandidate(''); setMethod('bank_transfer'); setUsers([]); setModal({ row, type });
    if (type === 'assign') { try { setUsers((await apiClient.get(`/finance/role-incentives/beneficiaries/${row.role}`)).data.users); } catch { toast.error('Could not load staff'); } }
  };

  const save = async e => {
    e.preventDefault(); if (busy) return; setBusy(true);
    const { id } = modal.row, url = `/finance/role-incentives/${encodeURIComponent(id)}`;
    try {
      if (modal.type === 'assign') await apiClient.put(`${url}/beneficiary`, { user_id: candidate, reason: reference });
      else if (modal.type === 'reject') await apiClient.post(`${url}/reject`, { reason: reference });
      else if (modal.type === 'reopen') await apiClient.post(`${url}/reopen`, { reason: reference });
      else if (modal.type === 'dismiss') await apiClient.post(`${url}/dismiss`, { reason: reference });
      else if (modal.type === 'reverse') await apiClient.post(`${url}/reverse`, { reason: reference });
      else await apiClient.post(`${url}/pay`, { payment_method: method, payment_reference: reference });
      const msgs = { assign: 'Staff assigned', reject: 'Incentive rejected', reopen: 'Incentive re-opened', dismiss: 'Marked not applicable', reverse: 'Reversal entry recorded', pay: 'Payment recorded' };
      setModal(null); toast.success(msgs[modal.type]); await load(); window.dispatchEvent(new Event('incentives-changed'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setBusy(false); }
  };

  const totals = data?.totals || {};
  const totalCards = personal
    ? [['Eligible – Unpaid', incentiveMoney(totals.eligible_unpaid), 'eligible'], ['Total Paid', incentiveMoney(totals.total_paid), 'paid']]
    : [['Eligible – Unpaid', incentiveMoney(totals.eligible_unpaid), 'eligible'], ['Total Paid', incentiveMoney(totals.total_paid), 'paid'], ['Pending Review', incentiveMoney(totals.pending_review), 'pending-review']];

  return <section className="space-y-4" data-testid="role-incentive-ledger">
    <div className="flex items-center justify-between gap-3">
      <h2 className="text-lg font-semibold" data-testid="role-ledger-title">{personal ? 'My base incentives' : 'Base incentives'}</h2>
      <Button variant="outline" onClick={load} data-testid="role-ledger-refresh">Refresh</Button>
    </div>

    <div className={`grid grid-cols-2 ${personal ? '' : 'sm:grid-cols-3'} gap-3`} data-testid="role-ledger-totals">
      {data && totalCards.map(([label, value, key]) => <div key={key} className="border rounded-lg px-3 py-2.5">
        <span className="text-xs text-slate-500" data-testid={`role-ledger-label-${key}`}>{label}</span>
        <p className="font-semibold text-base" data-testid={`role-ledger-${key}`}>{value}</p>
      </div>)}
    </div>

    {office && <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="role-ledger-filters">
        <select aria-label="Incentive role" className="min-h-[44px] border rounded px-2 min-w-0" value={role} onChange={e => { setRole(e.target.value); setEmployee('all'); }} data-testid="role-ledger-role"><option value="all">All roles</option>{INCENTIVE_ROLES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select>
        <select aria-label="Employee" className="min-h-[44px] border rounded px-2 min-w-0" value={employee} onChange={e => setEmployee(e.target.value)} data-testid="role-ledger-employee"><option value="all">All employees</option>{[...new Map((data?.employees || []).map(e => [e.id, e])).values()].map(e => <option key={e.id} value={e.id}>{e.name}</option>)}</select>
        <select aria-label="Incentive status" className="min-h-[44px] border rounded px-2 min-w-0" value={state} onChange={e => setState(e.target.value)} data-testid="role-ledger-status">{[['all', 'All statuses'], ['eligible', 'Under review / approved'], ['paid', 'Paid'], ['pending_configuration', 'Awaiting rate'], ['pending_assignee', 'Awaiting employee'], ['not_applicable', 'Not applicable (no staff)']].map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
      </div>
      {(data?.employee_totals || []).length > 0 && <div className="border rounded-lg">
        <button type="button" className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium" onClick={() => setShowEmployees(v => !v)} data-testid="role-ledger-employee-totals-toggle">
          <span>Employee-wise totals ({data.employee_totals.length})</span><span>{showEmployees ? '−' : '+'}</span>
        </button>
        {showEmployees && <div className="divide-y" data-testid="role-ledger-employee-totals">
          {data.employee_totals.map(e => <div key={`${e.id}:${e.role}`} className="flex items-center justify-between px-3 py-2 text-sm gap-2" data-testid={`role-emp-total-${e.id}-${e.role}`}>
            <span className="min-w-0 break-words"><strong>{e.name}</strong> <span className="text-xs text-slate-500">· {roleName(e.role)}</span></span>
            <span className="text-xs text-slate-600 whitespace-nowrap">Unpaid {incentiveMoney(e.eligible_unpaid)} · Paid {incentiveMoney(e.total_paid)}</span>
          </div>)}
        </div>}
      </div>}
    </div>}

    {error ? <p role="alert" data-testid="role-ledger-error">Could not load incentives. Use Refresh to retry.</p>
      : !data ? <p data-testid="role-ledger-loading">Loading incentives…</p>
        : <div className="space-y-3" data-testid="role-incentive-students">
          {students.map(s => <RoleIncentiveStudentCard key={s.portal_id} student={s} canReview={canReview} canPay={canPay} personal={personal} onAction={act} />)}
          {students.length === 0 && <p data-testid="role-incentives-empty">{personal ? 'No eligible or paid base incentives yet.' : 'No flown students match.'}</p>}
          {data.has_more && <div className="flex justify-center pt-1"><Button variant="outline" onClick={loadMore} disabled={loadingMore} data-testid="role-ledger-load-more">{loadingMore ? 'Loading…' : `Load more (${students.length} of ${data.total_students})`}</Button></div>}
        </div>}

    {modal && <RoleIncentiveDialog modal={modal} busy={busy} onClose={() => setModal(null)} onSave={save} reference={reference} setReference={setReference} candidate={candidate} setCandidate={setCandidate} method={method} setMethod={setMethod} users={users} />}
  </section>;
};
